import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Wand2, Briefcase, CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import type { ParsedJob, MetricQuestion } from "@/pages/ResumeQuantification";

interface QuantificationVerifyStepProps {
  parsedJobs: ParsedJob[];
  questions: Record<string, MetricQuestion[]>;
  answers: Record<string, Record<string, string>>;
  onComplete: (rewrittenBullets: Record<string, Array<{
    bulletIndex: number;
    original: string;
    rewritten: string;
    numberAdded: string | null;
  }>>) => void;
  onBack: () => void;
}

export function QuantificationVerifyStep({
  parsedJobs,
  questions,
  answers,
  onComplete,
  onBack,
}: QuantificationVerifyStepProps) {
  const { toast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);

  // Count answers
  const getJobAnswerSummary = (jobId: string) => {
    const jobQuestions = questions[jobId] || [];
    const jobAnswers = answers[jobId] || {};
    const answered = Object.values(jobAnswers).filter(a => a && a.trim() !== "").length;
    return { total: jobQuestions.length, answered };
  };

  const totalAnswered = Object.keys(answers)
    .reduce((sum, jobId) => sum + getJobAnswerSummary(jobId).answered, 0);

  const handleRewrite = async () => {
    if (totalAnswered === 0) {
      toast({
        title: "No answers provided",
        description: "Please answer at least one question to quantify your bullets.",
        variant: "destructive",
      });
      return;
    }

    setIsProcessing(true);
    setProcessingProgress(0);

    try {
      // Progress simulation
      const progressInterval = setInterval(() => {
        setProcessingProgress(prev => Math.min(prev + 10, 90));
      }, 500);

      // Build the jobs payload with answers
      const jobsPayload = parsedJobs.map(job => {
        const jobQuestions = questions[job.id] || [];
        const jobAnswers = answers[job.id] || {};
        
        // Map answers to include question context
        const answersWithContext: Record<string, { question: string; answer: string; bulletIndex: number }> = {};
        jobQuestions.forEach(q => {
          const answer = jobAnswers[q.id];
          if (answer && answer.trim()) {
            answersWithContext[q.id] = {
              question: q.question,
              answer: answer.trim(),
              bulletIndex: q.bulletIndex,
            };
          }
        });

        return {
          id: job.id,
          title: job.title,
          company: job.company,
          bullets: job.bullets,
          answers: answersWithContext,
        };
      });

      const { data, error } = await supabase.functions.invoke('ai-bullet-rewriter', {
        body: { jobs: jobsPayload }
      });

      clearInterval(progressInterval);

      if (error) throw new Error(error.message);
      if (!data.success) throw new Error(data.error || "Failed to rewrite bullets");

      setProcessingProgress(100);

      // Transform results into expected format
      const rewrittenBullets: Record<string, Array<{
        bulletIndex: number;
        original: string;
        rewritten: string;
        numberAdded: string | null;
      }>> = {};

      data.results.forEach((jobResult: any) => {
        rewrittenBullets[jobResult.jobId] = jobResult.results;
      });

      toast({
        title: "Bullets rewritten!",
        description: "Your resume bullets have been enhanced with your metrics.",
      });

      setTimeout(() => onComplete(rewrittenBullets), 500);

    } catch (error) {
      console.error('Rewriting error:', error);
      toast({
        title: "Rewriting failed",
        description: error instanceof Error ? error.message : "Failed to rewrite bullets",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  if (isProcessing) {
    return (
      <div className="text-center py-12">
        <Loader2 className="h-12 w-12 text-primary mx-auto mb-6 animate-spin" />
        <h2 className="text-xl font-semibold mb-4">Rewriting Your Bullets...</h2>
        <p className="text-muted-foreground mb-6">Adding your metrics naturally into each bullet</p>
        <div className="max-w-md mx-auto">
          <Progress value={processingProgress} className="h-3 mb-2" />
          <p className="text-sm text-muted-foreground">{Math.round(processingProgress)}%</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <Wand2 className="h-12 w-12 text-primary mx-auto mb-4" />
        <h2 className="text-2xl font-semibold mb-2">Verify Your Answers</h2>
        <p className="text-muted-foreground">
          Review your answers before we rewrite your bullets
        </p>
      </div>

      {/* Summary Stats */}
      <Card className="p-4 bg-primary/5 border-primary/20">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CheckCircle className="h-5 w-5 text-primary" />
            <span className="font-medium">Total Metrics Provided</span>
          </div>
          <Badge className="text-lg px-3 py-1">{totalAnswered}</Badge>
        </div>
      </Card>

      {/* Job Summaries */}
      <div className="space-y-4">
        {parsedJobs.map((job) => {
          const { total, answered } = getJobAnswerSummary(job.id);
          const jobAnswers = answers[job.id] || {};
          const jobQuestions = questions[job.id] || [];
          
          // Get answered questions with their values
          const answeredQuestions = jobQuestions.filter(q => {
            const ans = jobAnswers[q.id];
            return ans && ans.trim() !== "";
          });

          return (
            <Card key={job.id} className="p-5">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-muted rounded-lg">
                    <Briefcase className="h-4 w-4" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{job.title}</h3>
                    <p className="text-sm text-muted-foreground">{job.company}</p>
                  </div>
                </div>
                <Badge variant={answered > 0 ? "default" : "secondary"}>
                  {answered} / {total} answered
                </Badge>
              </div>

              {answered > 0 ? (
                <div className="space-y-2 pl-11">
                  {answeredQuestions.map((q) => (
                    <div key={q.id} className="flex items-center justify-between text-sm py-2 border-b border-muted last:border-0">
                      <span className="text-muted-foreground">{q.question}</span>
                      <span className="font-semibold text-primary">{jobAnswers[q.id]}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center gap-2 text-sm text-muted-foreground pl-11">
                  <AlertCircle className="h-4 w-4" />
                  <span>No metrics provided - bullets will remain unchanged</span>
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {/* Navigation */}
      <div className="flex justify-between items-center pt-6 border-t">
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Edit Answers
        </Button>
        <Button onClick={handleRewrite} size="lg" className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700">
          <Wand2 className="h-4 w-4 mr-2" />
          Rewrite My Bullets
        </Button>
      </div>
    </div>
  );
}
